// One open-chat answer (#2725): the words in a SAFE markdown subset, then the
// tables code drew from the read, then the dated sources.
//
// What may render, and why:
//   - bold, lists, numbered steps, one small heading, small tables. No raw
//     HTML: react-markdown skips it (no rehype-raw here, ever), and images
//     never render (one would load the moment it appeared: a tracking pixel
//     an injected instruction could plant).
//   - links: a path on our own site renders as an in-app link; an https link
//     renders only when the server kept it (it checked the link against this
//     turn's reads and the official-host rule) AND it is https here too.
//     Anything else is its words.
//   - source chips and our pages come from the server (the records the turn
//     read), never from the model's words.
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, LinkIcon } from '@heroicons/react/24/outline';
import AstraAvatar from '../../concierge/AstraAvatar';
import { CHAT } from '../../../content/astraChat';
import { FROM_ASTRA } from '../../../services/astraChatThread';
import { REPLAY_PRIVATE_CLASS } from '../../../services/analyticsPrivacy';

export const isInternal = (href) => typeof href === 'string' && /^\/[a-z0-9]/i.test(href) && !href.startsWith('//');
// The client's own check, mirroring the server's official_url refusals so a
// drift there can never open a link here: https only, no credentials, no
// backslash, an ASCII host with a dot (security review of #2725).
export function isExternalSafe(href) {
    if (typeof href !== 'string' || href.length > 400 || href.includes('\\') || !/^https:\/\//i.test(href)) return false;
    try {
        const u = new URL(href);
        return u.protocol === 'https:' && !u.username && !u.password
            && /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(u.hostname);
    } catch {
        return false;
    }
}

const MD = {
    a: ({ href, children }) => {
        if (isInternal(href)) {
            return <Link to={href} state={FROM_ASTRA} className="font-semibold text-[#1A4D2E] underline underline-offset-2">{children}</Link>;
        }
        if (isExternalSafe(href)) {
            return (
                <a href={href} target="_blank" rel="noopener noreferrer nofollow"
                    className="font-semibold text-[#1A4D2E] underline underline-offset-2">{children}</a>
            );
        }
        return <span>{children}</span>;
    },
    img: () => null,
    h1: ({ children }) => <p className="font-serif text-base font-semibold text-[#1A4D2E]">{children}</p>,
    h2: ({ children }) => <p className="font-serif text-base font-semibold text-[#1A4D2E]">{children}</p>,
    h3: ({ children }) => <p className="font-serif text-base font-semibold text-[#1A4D2E]">{children}</p>,
    h4: ({ children }) => <p className="font-semibold text-[#1A4D2E]">{children}</p>,
    ul: ({ children }) => <ul className="list-disc space-y-1 pl-5">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal space-y-1 pl-5">{children}</ol>,
    table: ({ children }) => (
        <div className="-mx-1 overflow-x-auto"><table className="w-full min-w-[240px] border-collapse text-[13px]">{children}</table></div>
    ),
    th: ({ children }) => <th className="border-b border-[#CFE0D2] px-2 py-1.5 text-left font-semibold text-[#1A4D2E]">{children}</th>,
    td: ({ children }) => <td className="border-b border-[#E8EFE8] px-2 py-1.5">{children}</td>,
};

// A table row's first cell (#2874): the school name links to its page on our
// site and carries a small official-source link, both from the server's
// `links[ri]` (aligned with `rows[ri]`), each passing the same checks the
// chips use. Anything that fails a check is the plain words, as before.
function FirstCell({ value, link, onSource }) {
    const text = value ?? '-';
    // only a college page: the one kind of row the server sends
    const page = isInternal(link?.page) && /^\/colleges\/[a-z0-9_-]+(#[a-z-]+)?$/.test(link.page) ? link.page : null;
    const source = isExternalSafe(link?.source) ? link.source : null;
    if (!page && !source) return text;
    return (
        <span className="inline-flex flex-wrap items-center gap-x-1">
            {page ? (
                <Link to={page} state={FROM_ASTRA} onClick={() => onSource?.('stratia_page')} data-testid="astra-table-page"
                    className="text-[#1A4D2E] underline underline-offset-2">{text}</Link>
            ) : text}
            {source && (
                <a href={source} target="_blank" rel="noopener noreferrer nofollow" onClick={() => onSource?.('official')}
                    aria-label={`${text} source`} data-testid="astra-table-source"
                    className="-my-1 inline-flex items-center rounded p-1 text-[#2D6B45] hover:bg-[#E8F0E9]">
                    <LinkIcon className="h-3 w-3 shrink-0" aria-hidden="true" />
                </a>
            )}
        </span>
    );
}

export function DataTable({ t, onSource }) {
    if (!t || !Array.isArray(t.head) || !Array.isArray(t.rows) || !t.rows.every(Array.isArray)) return null;
    const links = Array.isArray(t.links) ? t.links : null;
    return (
        <figure className="space-y-1" data-testid={`astra-table-${t.kind}`}>
            {t.title && <figcaption className="text-xs font-semibold text-[#4A6B53]">{t.title}</figcaption>}
            <div className="-mx-1 overflow-x-auto">
                <table className="w-full min-w-[260px] border-collapse text-[13px]">
                    <thead>
                        <tr>{t.head.map((h, i) => <th key={`h${i}`} scope="col" className="border-b border-[#CFE0D2] px-2 py-1.5 text-left font-semibold text-[#1A4D2E]">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                        {t.rows.map((r, ri) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <tr key={`r${ri}`} className="odd:bg-white/60">
                                {r.map((c, i) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <td key={`c${i}`} className={`border-b border-[#E8EFE8] px-2 py-1.5 ${i === 0 ? 'font-medium' : 'tabular-nums'}`}>
                                        {i === 0 && links ? <FirstCell value={c} link={links[ri]} onSource={onSource} /> : (c ?? '-')}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </figure>
    );
}

export function SourceChips({ sources, pages, onSource }) {
    if (!(sources || []).length && !(pages || []).length) return null;
    return (
        <div className="mt-2.5 flex flex-wrap gap-1.5" data-testid="astra-sources" aria-label={CHAT.sourcesLabel}>
            {(sources || []).map((s) => (isExternalSafe(s.url) ? (
                <a key={`${s.label}-${s.url}`} href={s.url} target="_blank" rel="noopener noreferrer nofollow"
                    onClick={() => onSource?.(s.kind)} data-testid="astra-source"
                    className="inline-flex min-h-[44px] items-center gap-1 rounded-full border border-[#D8E6DA] bg-white px-2.5 py-1 text-[11px] text-[#2D6B45] underline-offset-2 hover:underline">
                    <LinkIcon className="h-3 w-3 shrink-0" aria-hidden="true" />
                    <span>{s.label}</span>{s.when && <span className="whitespace-nowrap text-[#6B6B6B]">· {s.when}</span>}
                </a>
            ) : (
                <span key={`${s.label}-${s.kind}`} data-testid="astra-source"
                    className="inline-flex items-center gap-1 rounded-full border border-dashed border-[#E0DED8] px-2.5 py-1 text-[11px] text-[#6B6B6B]">
                    {s.label} <span className="whitespace-nowrap">· {s.when || CHAT.noLink}</span>
                </span>
            )))}
            {(pages || []).filter((p) => isInternal(p.to)).map((p) => (
                <Link key={p.to} to={p.to} state={FROM_ASTRA} onClick={() => onSource?.('stratia_page')} data-testid="astra-page"
                    className="inline-flex min-h-[44px] items-center gap-1 rounded-full bg-[#E8F0E9] px-2.5 py-1 text-[11px] font-semibold text-[#1A4D2E]">
                    {p.label} <ArrowRightIcon className="h-3 w-3" aria-hidden="true" />
                </Link>
            ))}
        </div>
    );
}

// Blocked in session replay (#4475), testid included: a reply may be the
// 988 card, and its testid alone would say so (#4294).
export default function RichAnswer({ answer, testid, onSource }) {
    return (
        <div className={`${REPLAY_PRIVATE_CLASS} flex items-start gap-2`} data-testid={testid}>
            <AstraAvatar size={28} className="mt-0.5" />
            <div className="min-w-0 max-w-[92%] rounded-2xl bg-[#F4F8F4] px-3.5 py-2.5 text-left text-sm leading-relaxed text-[#2C2C2C]">
                <div className="astra-md space-y-2 [&_strong]:font-semibold">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD} skipHtml>{answer.text || ''}</ReactMarkdown>
                    {(answer.tables || []).map((t) => <DataTable key={`${t.kind}-${t.title}`} t={t} onSource={onSource} />)}
                </div>
                <SourceChips sources={answer.sources} pages={answer.pages} onSource={onSource} />
            </div>
        </div>
    );
}
